/**
 * The hand-off of the MFA partial-auth token from Login.tsx to the MFA pages,
 * and the way back to sign-in (2026-09-27, tracker decision 62).
 *
 * Login.tsx puts the five-minute partial-auth token in the URL FRAGMENT
 * (`/auth/mfa-enroll#t=...` or `/auth/mfa-challenge#t=...`): the fragment is
 * never sent to a server or in a Referer, and it is the only carrier that also
 * crosses from the apex host to the tenant subdomain (sessionStorage is per
 * origin). The page reads it once, keeps it in memory, and removes it from the
 * address bar straight away, as Bridge.tsx and SsoLanding.tsx do with theirs,
 * so it does not sit in history or on screen. A reload therefore loses it;
 * the page then offers the way back to sign-in.
 *
 * To the API the token goes only as `Authorization: Bearer` (lib/api.ts).
 */
import type { MfaSignInAgainReason } from '@/lib/api';

/** Query parameter Login.tsx reads to say why the person is back. */
export const MFA_ERROR_PARAM = 'mfa_error';

/** The MFA page for the step the API named: enrol first, or challenge an enrolled factor. */
export function mfaStepPath(reason: 'mfa_required' | 'mfa_enrolment_due'): '/auth/mfa-enroll' | '/auth/mfa-challenge' {
  return reason === 'mfa_enrolment_due' ? '/auth/mfa-enroll' : '/auth/mfa-challenge';
}

/**
 * Where a sign-in that still owes a second factor goes: the MFA page, with the
 * partial-auth token in the fragment. `origin` is `https://<slug>.<apex>` when
 * the page must cross to the tenant host (the apex login, a sign-in link), or
 * '' to stay on this origin. Used by Login.tsx and, since the reset and
 * first-login links stopped minting a session for an account with MFA
 * (decision 65), by FirstLogin.tsx.
 */
export function mfaHandoffTarget(
  reason: 'mfa_required' | 'mfa_enrolment_due',
  partialAuthToken: string,
  origin: string,
): string {
  return `${origin}${mfaStepPath(reason)}#t=${encodeURIComponent(partialAuthToken)}`;
}

/**
 * Full-page navigation to the MFA page. An object, so a test can replace
 * `replace` (jsdom cannot navigate, and `window.location.replace` cannot be
 * spied on).
 */
export const mfaNavigation = {
  replace(url: string): void {
    window.location.replace(url);
  },
};

/** The partial-auth token in `#t=`, or null. Pure: safe in a useState initialiser. */
export function readPartialAuthFromHash(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;
  const token = new URLSearchParams(hash).get('t');
  return token && token.length > 0 ? token : null;
}

/** Remove the fragment (and the token in it) from the address bar and this history entry. */
export function scrubPartialAuthFromUrl(): void {
  if (typeof window === 'undefined' || !window.location.hash) return;
  window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
}

/** What Login.tsx shows for each `?mfa_error=` value. */
export const MFA_SIGN_IN_AGAIN_COPY: Record<MfaSignInAgainReason, string> = {
  expired:
    'Your sign-in timed out before the verification step was finished (it lasts five minutes). Sign in again to continue.',
  invalid: 'That verification step is no longer valid. Sign in again to continue.',
  suspended: 'This account is suspended. Ask your administrator to reinstate it.',
  role: 'This account cannot sign in to this workspace. Ask your administrator to assign it a workspace role.',
  locked:
    'Too many incorrect verification codes. Verification is locked for a while (15 minutes at first); try again later, or ask your administrator to reset your MFA.',
};

export function isMfaSignInAgainReason(value: string | null): value is MfaSignInAgainReason {
  return value !== null && Object.prototype.hasOwnProperty.call(MFA_SIGN_IN_AGAIN_COPY, value);
}
